import type {
  AdminContactMessage,
  AdminCounts,
  AdminDashboard,
  AdminLiveComment,
  AdminLiveDashboard,
  AdminPartner,
  AdminRsvp,
  AdminSubscriber,
  AdminCreateSubscriberInput,
  AdminUpdateSubscriberInput,
  SubscriberBroadcastInput,
  SubscriberBroadcastResult,
  AdminTestimonial,
  BlogPost,
  BlogPostInput,
  BlogPostSummary,
  SitePage,
  SitePageInput,
  ContactInput,
  EventContent,
  EventStreamConfig,
  LiveComment,
  LiveCommentInput,
  LiveCommentStatus,
  LiveInfo,
  LivePageData,
  LoginInput,
  PartnerBody,
  AdminCreatePartnerInput,
  AdminCreateTestimonialInput,
  AdminUpdatePartnerInput,
  PublicUser,
  RegisterInput,
  RequestStatus,
  PublicCheckInBody,
  RsvpBody,
  SiteSettings,
  Stats,
  SubscribeBody,
  Testimonial,
  TestimonialInput,
  TestimonialStatus,
  ParticipationToday,
  ParticipationPromptPublic,
  ParticipationWallItem,
  ParticipationStats,
  ParticipationSchedule,
  AdminParticipationResponse,
  ParticipationResponseStatus,
  AdminUpdateParticipationPromptInput,
  ModerateParticipationResponseInput,
  ParticipationOpenTextInput,
  ParticipationYesNoInput,
  ParticipationPastoralInput,
  ParticipationAggregateTapInput,
  UpdateSiteSettingsInput,
  UpdateFooterSettingsInput,
  FooterSettings,
  UpdateEmailSettingsInput,
  EmailSettingsPublic,
  UpdateYoutubeSettingsInput,
  YoutubeSettingsPublic,
  UpdateSmsSettingsInput,
  SmsSettingsPublic,
  SendSmsInput,
  SendSmsResult,
  TestSmsInput,
  AdminContact,
  AdminContactsPayload,
  AdminCreateContactInput,
  AdminUpdateContactInput,
  AdminImportContactsInput,
  AdminBulkContactsInput,
  ContactImportResult,
  ContactSyncResult,
  AdminContactMerge,
  ContactMergeResult,
  AdminMergeContactsInput,
  AdminContactDetail,
  AdminSyncContactsInput,
  MemberCheckInResult,
  SendSmsBroadcastInput,
  SmsBroadcastTarget,
  SmsCampaign,
  SmsBudgetPayload,
  SmsMessageLogEntry,
  SmsTopUpInput,
  EmailAttendanceReportInput,
  EmailAttendanceReportResult,
  YoutubeChannelVideo,
  SiteVideoPublic,
  UpdateYoutubeVideoSiteMetaInput,
  AdminUser,
  AdminCreateUserInput,
  AdminUpdateUserInput,
  AdminResetPasswordInput,
  CreateScheduledEventInput,
  FeaturedEventInfo,
  AdminEventHubSummary,
  ScheduledEvent,
  UpdateScheduledEventInput,
  SiteCopy,
  GalleryAlbumSummary,
  GalleryAlbumDetail,
  GalleryPhoto,
  PhotoComment,
  PhotoCommentInput,
  PhotoCommentStatus,
  AdminGalleryAlbum,
  AdminGalleryPhoto,
  AdminPhotoComment,
  CreateGalleryAlbumInput,
  UpdateGalleryAlbumInput,
  CreateGalleryPhotoInput,
  UpdateGalleryPhotoInput,
  SiteContactLink,
  PublicConfig,
  SiteContactLinkPublic,
  AdminCreateSiteContactLinkInput,
  AdminUpdateSiteContactLinkInput,
  SiteSocialLink,
  SiteSocialLinkPublic,
  AdminCreateSiteSocialLinkInput,
  AdminUpdateSiteSocialLinkInput,
  EventAttendee,
  EventAttendeeSummary,
  CreateEventAttendeeInput,
  UpdateEventAttendeeInput,
  BulkCreateEventAttendeesInput,
  MovementMilestone,
  AdminCreateMovementMilestoneInput,
  AdminUpdateMovementMilestoneInput,
} from "@tih/shared";

type Envelope<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; details?: unknown };

export class ApiError extends Error {
  status: number;
  details?: unknown;
  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  let body: Envelope<T>;
  try {
    body = (await res.json()) as Envelope<T>;
  } catch {
    throw new ApiError(`Bad response (${res.status})`, res.status);
  }
  if (!body.ok) {
    throw new ApiError(
      body.error || `Request failed (${res.status})`,
      res.status,
      body.details,
    );
  }
  return body.data;
}

// --- public ---

export async function fetchPublicConfig(): Promise<PublicConfig> {
  return request<PublicConfig>("/api/config/public");
}

export async function fetchStats(): Promise<Stats> {
  return request<Stats>("/api/stats");
}

export async function fetchEvent(): Promise<EventContent> {
  return request<EventContent>("/api/event");
}

export async function fetchSiteSettings(): Promise<SiteSettings> {
  return request<SiteSettings>("/api/site");
}

export async function fetchSiteCopy(): Promise<SiteCopy> {
  return request<SiteCopy>("/api/copy");
}

export async function fetchMovementMilestones(): Promise<MovementMilestone[]> {
  return request<MovementMilestone[]>("/api/movement/milestones");
}

export async function fetchSiteContacts(): Promise<SiteContactLinkPublic[]> {
  return request<SiteContactLinkPublic[]>("/api/contacts");
}

export async function fetchSiteSocialLinks(): Promise<SiteSocialLinkPublic[]> {
  return request<SiteSocialLinkPublic[]>("/api/social");
}

export async function fetchFooterSettings(): Promise<FooterSettings> {
  return request<FooterSettings>("/api/footer");
}

export async function postSubscribe(body: SubscribeBody): Promise<{ id: number | null }> {
  return request<{ id: number | null }>("/api/subscribe", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function postRsvp(body: RsvpBody): Promise<{ id: number | null }> {
  return request<{ id: number | null }>("/api/rsvp", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function postPublicCheckIn(
  body: PublicCheckInBody,
): Promise<{ id: number; eventId: number; source: "online" }> {
  return request<{ id: number; eventId: number; source: "online" }>("/api/check-in", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function postPartner(body: PartnerBody): Promise<{ id: number | null }> {
  return request<{ id: number | null }>("/api/partner", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function postContact(body: ContactInput): Promise<{ id: number }> {
  return request<{ id: number }>("/api/contact", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// --- live ---

export async function fetchLive(): Promise<LivePageData> {
  return request<LivePageData>("/api/live");
}

export async function fetchLiveComments(): Promise<LiveComment[]> {
  return request<LiveComment[]>("/api/live/comments");
}

export async function postLiveComment(body: LiveCommentInput): Promise<LiveComment> {
  return request<LiveComment>("/api/live/comments", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function deleteLiveComment(id: number): Promise<void> {
  await request<unknown>(`/api/live/comments/${id}`, { method: "DELETE" });
}

export async function adminFetchLiveComments(
  status?: LiveCommentStatus,
): Promise<{ comments: AdminLiveComment[]; youtubeSyncEnabled: boolean }> {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return request<{ comments: AdminLiveComment[]; youtubeSyncEnabled: boolean }>(
    `/api/admin/comments${q}`,
  );
}

export async function adminModerateLiveComment(
  id: number,
  status: "PUBLISHED" | "REJECTED",
): Promise<void> {
  await request<unknown>(`/api/admin/comments/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function adminDeleteLiveComment(id: number): Promise<void> {
  await request<unknown>(`/api/admin/comments/${id}`, { method: "DELETE" });
}

export async function adminSyncYoutubeComments(): Promise<{
  synced: number;
  videos: string[];
}> {
  return request<{ synced: number; videos: string[] }>(
    "/api/admin/comments/sync-youtube",
    { method: "POST", body: JSON.stringify({}) },
  );
}

// --- auth ---

export async function fetchMe(): Promise<{ user: PublicUser | null }> {
  return request<{ user: PublicUser | null }>("/api/auth/me");
}

export async function postLogin(body: LoginInput): Promise<{ user: PublicUser }> {
  return request<{ user: PublicUser }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function postRegister(body: RegisterInput): Promise<{ user: PublicUser }> {
  return request<{ user: PublicUser }>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function postLogout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
}

// --- testimonials ---

export async function fetchTestimonials(): Promise<Testimonial[]> {
  return request<Testimonial[]>("/api/testimonials");
}

export async function postTestimonial(body: TestimonialInput): Promise<{ id: number }> {
  return request<{ id: number }>("/api/testimonials", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type ParticipationPromptsPayload = {
  prompts: ParticipationPromptPublic[];
  spotlightSlug: string | null;
  proximity: string;
  featuredEvent: ParticipationToday["featuredEvent"];
  daysOut: number | null;
  prepChecklist: ParticipationToday["prepChecklist"];
};

export async function fetchParticipationToday(): Promise<ParticipationToday> {
  return request<ParticipationToday>("/api/participation/today");
}

export async function fetchParticipationPrompts(): Promise<ParticipationPromptsPayload> {
  return request<ParticipationPromptsPayload>("/api/participation/prompts");
}

export async function fetchParticipationWall(
  limit = 30,
  prompt?: string,
): Promise<ParticipationWallItem[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (prompt) params.set("prompt", prompt);
  return request<ParticipationWallItem[]>(`/api/participation/wall?${params}`);
}

export async function fetchParticipationStats(slug: string): Promise<ParticipationStats> {
  return request<ParticipationStats>(`/api/participation/stats/${encodeURIComponent(slug)}`);
}

export async function postParticipationOpenText(
  body: ParticipationOpenTextInput,
): Promise<{ saved: boolean }> {
  return request<{ saved: boolean }>("/api/participation/responses", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function postParticipationYesNo(
  body: ParticipationYesNoInput,
): Promise<{ saved: boolean }> {
  return request<{ saved: boolean }>("/api/participation/responses", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function postParticipationPastoral(
  body: ParticipationPastoralInput,
): Promise<{ saved: boolean }> {
  return request<{ saved: boolean }>("/api/participation/responses", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function postParticipationAggregate(
  body: ParticipationAggregateTapInput,
): Promise<{ saved: boolean }> {
  return request<{ saved: boolean }>("/api/participation/responses", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminFetchParticipationPrompts(): Promise<ParticipationPromptPublic[]> {
  return request<ParticipationPromptPublic[]>("/api/admin/participation/prompts");
}

export async function adminUpdateParticipationPrompt(
  id: number,
  body: AdminUpdateParticipationPromptInput,
): Promise<ParticipationPromptPublic> {
  return request<ParticipationPromptPublic>(`/api/admin/participation/prompts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function adminFetchParticipationSchedule(): Promise<ParticipationSchedule> {
  return request<ParticipationSchedule>("/api/admin/participation/schedule");
}

export async function adminSaveParticipationSchedule(
  body: Partial<ParticipationSchedule>,
): Promise<ParticipationSchedule> {
  return request<ParticipationSchedule>("/api/admin/participation/schedule", {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function adminFetchParticipationResponses(options?: {
  status?: ParticipationResponseStatus;
  kind?: string;
}): Promise<AdminParticipationResponse[]> {
  const params = new URLSearchParams();
  if (options?.status) params.set("status", options.status);
  if (options?.kind) params.set("kind", options.kind);
  const q = params.toString();
  return request<AdminParticipationResponse[]>(
    `/api/admin/participation/responses${q ? `?${q}` : ""}`,
  );
}

export async function adminModerateParticipationResponse(
  id: number,
  body: ModerateParticipationResponseInput,
): Promise<AdminParticipationResponse> {
  return request<AdminParticipationResponse>(`/api/admin/participation/responses/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function uploadTestimonialPhoto(file: File): Promise<{ uploadsUrl: string }> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch("/api/testimonials/upload-photo", {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  let body: Envelope<{ uploadsUrl: string }>;
  try {
    body = (await res.json()) as Envelope<{ uploadsUrl: string }>;
  } catch {
    throw new ApiError(`Bad response (${res.status})`, res.status);
  }
  if (!body.ok) {
    throw new ApiError(
      body.error || `Upload failed (${res.status})`,
      res.status,
      body.details,
    );
  }
  return body.data;
}

// --- blog (public) ---

export async function fetchBlogPosts(): Promise<BlogPostSummary[]> {
  return request<BlogPostSummary[]>("/api/blog");
}

export async function fetchBlogPost(slug: string): Promise<BlogPost> {
  return request<BlogPost>(`/api/blog/${encodeURIComponent(slug)}`);
}

// --- admin ---

export async function adminFetchCounts(): Promise<AdminCounts> {
  return request<AdminCounts>("/api/admin/counts");
}

export async function adminFetchDashboard(): Promise<AdminDashboard> {
  return request<AdminDashboard>("/api/admin/dashboard");
}

export async function adminFetchTestimonials(
  status?: TestimonialStatus,
): Promise<AdminTestimonial[]> {
  const q = status ? `?status=${status}` : "";
  return request<AdminTestimonial[]>(`/api/admin/testimonials${q}`);
}

export async function adminModerateTestimonial(
  id: number,
  status: "PUBLISHED" | "REJECTED",
): Promise<{ id: number; status: TestimonialStatus }> {
  return request(`/api/admin/testimonials/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function adminDeleteTestimonial(id: number): Promise<void> {
  await request<unknown>(`/api/admin/testimonials/${id}`, { method: "DELETE" });
}

export async function adminCreateTestimonial(
  body: AdminCreateTestimonialInput,
): Promise<AdminTestimonial> {
  return request<AdminTestimonial>("/api/admin/testimonials", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminFetchMovementMilestones(): Promise<MovementMilestone[]> {
  return request<MovementMilestone[]>("/api/admin/movement/milestones");
}

export async function adminCreateMovementMilestone(
  body: AdminCreateMovementMilestoneInput,
): Promise<MovementMilestone> {
  return request<MovementMilestone>("/api/admin/movement/milestones", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminUpdateMovementMilestone(
  id: number,
  body: AdminUpdateMovementMilestoneInput,
): Promise<MovementMilestone> {
  return request<MovementMilestone>(`/api/admin/movement/milestones/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function adminDeleteMovementMilestone(id: number): Promise<void> {
  await request<unknown>(`/api/admin/movement/milestones/${id}`, { method: "DELETE" });
}

export async function adminFetchRsvps(options?: {
  eventId?: number | null;
}): Promise<AdminRsvp[]> {
  const params = new URLSearchParams();
  if (options?.eventId === null) params.set("eventId", "unassigned");
  else if (options?.eventId != null) params.set("eventId", String(options.eventId));
  const q = params.toString();
  return request<AdminRsvp[]>(`/api/admin/rsvps${q ? `?${q}` : ""}`);
}

export async function adminUpdateRsvp(
  id: number,
  status: RequestStatus,
  notes?: string,
) {
  return request(`/api/admin/rsvps/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status, notes }),
  });
}

export async function adminFetchPartners(
  status?: RequestStatus,
): Promise<AdminPartner[]> {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return request<AdminPartner[]>(`/api/admin/partners${q}`);
}

export async function adminCreatePartner(
  body: AdminCreatePartnerInput,
): Promise<AdminPartner> {
  return request<AdminPartner>("/api/admin/partners", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminUpdatePartner(
  id: number,
  patch: AdminUpdatePartnerInput,
): Promise<AdminPartner> {
  return request<AdminPartner>(`/api/admin/partners/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function adminDeletePartner(id: number): Promise<void> {
  await request<unknown>(`/api/admin/partners/${id}`, { method: "DELETE" });
}

export async function adminFetchSubscribers(): Promise<{
  subscribers: AdminSubscriber[];
  smtpConfigured: boolean;
}> {
  return request<{ subscribers: AdminSubscriber[]; smtpConfigured: boolean }>(
    "/api/admin/subscribers",
  );
}

export async function adminCreateSubscriber(
  body: AdminCreateSubscriberInput,
): Promise<AdminSubscriber> {
  return request<AdminSubscriber>("/api/admin/subscribers", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminUpdateSubscriber(
  id: number,
  patch: AdminUpdateSubscriberInput,
): Promise<AdminSubscriber> {
  return request<AdminSubscriber>(`/api/admin/subscribers/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function adminDeleteSubscriber(id: number): Promise<void> {
  await request<unknown>(`/api/admin/subscribers/${id}`, { method: "DELETE" });
}

export async function adminBroadcastToSubscribers(
  body: SubscriberBroadcastInput,
): Promise<SubscriberBroadcastResult> {
  return request<SubscriberBroadcastResult>("/api/admin/subscribers/broadcast", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminFetchContact(): Promise<AdminContactMessage[]> {
  return request<AdminContactMessage[]>("/api/admin/contact");
}

export async function adminUpdateContact(
  id: number,
  status: RequestStatus,
  notes?: string,
) {
  return request(`/api/admin/contact/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status, notes }),
  });
}

export async function adminFetchSiteContacts(): Promise<SiteContactLink[]> {
  return request<SiteContactLink[]>("/api/admin/site-contacts");
}

export async function adminCreateSiteContactLink(
  body: AdminCreateSiteContactLinkInput,
): Promise<SiteContactLink> {
  return request<SiteContactLink>("/api/admin/site-contacts", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminUpdateSiteContactLink(
  id: number,
  body: AdminUpdateSiteContactLinkInput,
): Promise<SiteContactLink> {
  return request<SiteContactLink>(`/api/admin/site-contacts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function adminDeleteSiteContactLink(id: number): Promise<void> {
  await request<unknown>(`/api/admin/site-contacts/${id}`, { method: "DELETE" });
}

export async function adminReorderSiteContactLinks(
  linkIds: number[],
): Promise<SiteContactLink[]> {
  return request<SiteContactLink[]>("/api/admin/site-contacts/reorder", {
    method: "PATCH",
    body: JSON.stringify({ linkIds }),
  });
}

export async function adminFetchSiteSocialLinks(): Promise<SiteSocialLink[]> {
  return request<SiteSocialLink[]>("/api/admin/site-social");
}

export async function adminCreateSiteSocialLink(
  body: AdminCreateSiteSocialLinkInput,
): Promise<SiteSocialLink> {
  return request<SiteSocialLink>("/api/admin/site-social", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminUpdateSiteSocialLink(
  id: number,
  body: AdminUpdateSiteSocialLinkInput,
): Promise<SiteSocialLink> {
  return request<SiteSocialLink>(`/api/admin/site-social/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function adminDeleteSiteSocialLink(id: number): Promise<void> {
  await request<unknown>(`/api/admin/site-social/${id}`, { method: "DELETE" });
}

export async function adminReorderSiteSocialLinks(
  linkIds: number[],
): Promise<SiteSocialLink[]> {
  return request<SiteSocialLink[]>("/api/admin/site-social/reorder", {
    method: "PATCH",
    body: JSON.stringify({ linkIds }),
  });
}

export async function adminFetchBlog(options?: {
  eventId?: number;
  generalOnly?: boolean;
}): Promise<BlogPost[]> {
  const params = new URLSearchParams();
  if (options?.generalOnly) params.set("general", "1");
  else if (options?.eventId != null) params.set("eventId", String(options.eventId));
  const q = params.toString();
  return request<BlogPost[]>(`/api/admin/blog${q ? `?${q}` : ""}`);
}

export async function adminFetchBlogPost(id: number): Promise<BlogPost> {
  return request<BlogPost>(`/api/admin/blog/${id}`);
}

export async function adminCreateBlogPost(input: BlogPostInput): Promise<BlogPost> {
  return request<BlogPost>("/api/admin/blog", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function adminUpdateBlogPost(
  id: number,
  input: BlogPostInput,
): Promise<BlogPost> {
  return request<BlogPost>(`/api/admin/blog/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function adminDeleteBlogPost(id: number): Promise<void> {
  await request<unknown>(`/api/admin/blog/${id}`, { method: "DELETE" });
}

export async function adminFetchPages(): Promise<SitePage[]> {
  return request<SitePage[]>("/api/admin/pages");
}

export async function adminFetchPage(id: number): Promise<SitePage> {
  return request<SitePage>(`/api/admin/pages/${id}`);
}

export async function adminCreatePage(input: SitePageInput): Promise<SitePage> {
  return request<SitePage>("/api/admin/pages", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function adminUpdatePage(id: number, input: SitePageInput): Promise<SitePage> {
  return request<SitePage>(`/api/admin/pages/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function adminDeletePage(id: number): Promise<void> {
  await request<unknown>(`/api/admin/pages/${id}`, { method: "DELETE" });
}

export async function fetchPage(slug: string): Promise<SitePage> {
  return request<SitePage>(`/api/pages/${encodeURIComponent(slug)}`);
}

export async function adminFetchContent(
  key: string,
): Promise<{ key: string; valueJson: unknown; updatedAt: string }> {
  return request(`/api/admin/content/${encodeURIComponent(key)}`);
}

export async function adminPutContent(
  key: string,
  valueJson: unknown,
): Promise<{ key: string; valueJson: unknown; updatedAt: string }> {
  return request(`/api/admin/content/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify({ valueJson }),
  });
}

export async function adminPatchContent(
  key: string,
  valueJson: unknown,
): Promise<{ key: string; valueJson: unknown; updatedAt: string }> {
  return request(`/api/admin/content/${encodeURIComponent(key)}`, {
    method: "PATCH",
    body: JSON.stringify({ valueJson }),
  });
}

export async function adminFetchLive(): Promise<AdminLiveDashboard & { viewers: number }> {
  return request<AdminLiveDashboard & { viewers: number }>("/api/admin/live");
}

export async function adminSaveEventStream(
  eventId: number,
  streamJson: EventStreamConfig | null,
): Promise<{
  info: LiveInfo;
  events: AdminLiveDashboard["events"];
  broadcastTo: number;
}> {
  return request<{
    info: LiveInfo;
    events: AdminLiveDashboard["events"];
    broadcastTo: number;
  }>(`/api/admin/live/events/${eventId}/stream`, {
    method: "PATCH",
    body: JSON.stringify(streamJson),
  });
}

export async function adminPushLiveState(): Promise<{
  info: LiveInfo;
  updatedAt: string;
  broadcastTo: number;
}> {
  return request<{ info: LiveInfo; updatedAt: string; broadcastTo: number }>(
    "/api/admin/live",
    { method: "PUT", body: JSON.stringify({}) },
  );
}

export async function adminFetchSiteSettings(): Promise<SiteSettings> {
  return adminFetchSettingsGeneral();
}

export async function adminPutSiteSettings(
  patch: UpdateSiteSettingsInput,
): Promise<SiteSettings> {
  return adminPutSettingsGeneral(patch);
}

export async function adminFetchSettingsGeneral(): Promise<SiteSettings> {
  return request<SiteSettings>("/api/admin/settings/general");
}

export async function adminPutSettingsGeneral(
  patch: UpdateSiteSettingsInput,
): Promise<SiteSettings> {
  return request<SiteSettings>("/api/admin/settings/general", {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export async function adminFetchSettingsFooter(): Promise<FooterSettings> {
  return request<FooterSettings>("/api/admin/settings/footer");
}

export async function adminPutSettingsFooter(
  patch: UpdateFooterSettingsInput,
): Promise<FooterSettings> {
  return request<FooterSettings>("/api/admin/settings/footer", {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export async function adminFetchSettingsEmail(): Promise<EmailSettingsPublic> {
  return request<EmailSettingsPublic>("/api/admin/settings/email");
}

export async function adminPutSettingsEmail(
  body: UpdateEmailSettingsInput,
): Promise<EmailSettingsPublic> {
  return request<EmailSettingsPublic>("/api/admin/settings/email", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function adminTestSettingsEmail(): Promise<{ sentTo: string }> {
  return request<{ sentTo: string }>("/api/admin/settings/email/test", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function adminFetchSettingsYoutube(): Promise<YoutubeSettingsPublic> {
  return request<YoutubeSettingsPublic>("/api/admin/settings/youtube");
}

export async function adminPutSettingsYoutube(
  body: UpdateYoutubeSettingsInput,
): Promise<YoutubeSettingsPublic> {
  return request<YoutubeSettingsPublic>("/api/admin/settings/youtube", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function adminTestSettingsYoutube(): Promise<{ channelTitle: string }> {
  return request<{ channelTitle: string }>("/api/admin/settings/youtube/test", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function adminFetchSettingsSms(): Promise<SmsSettingsPublic> {
  return request<SmsSettingsPublic>("/api/admin/settings/sms");
}

export async function adminPutSettingsSms(
  body: UpdateSmsSettingsInput,
): Promise<SmsSettingsPublic> {
  return request<SmsSettingsPublic>("/api/admin/settings/sms", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function adminTestSettingsSms(
  body: TestSmsInput,
): Promise<{ sentTo: string; costUgx?: number }> {
  return request<{ sentTo: string; costUgx?: number }>("/api/admin/settings/sms/test", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminEmailAttendanceReport(
  eventId: number,
  body: EmailAttendanceReportInput = {},
): Promise<EmailAttendanceReportResult> {
  return request<EmailAttendanceReportResult>(
    `/api/admin/attendance/${eventId}/email`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

export async function adminSendEventSms(body: SendSmsInput): Promise<SendSmsResult> {
  return request<SendSmsResult>("/api/admin/sms", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminSendSmsBroadcast(
  body: SendSmsBroadcastInput,
): Promise<SendSmsResult> {
  return request<SendSmsResult>("/api/admin/sms/broadcast", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminPreviewSmsRecipients(
  target: SmsBroadcastTarget,
): Promise<{ recipients: number }> {
  return request<{ recipients: number }>("/api/admin/sms/preview", {
    method: "POST",
    body: JSON.stringify({ target }),
  });
}

export async function adminFetchSmsCampaigns(): Promise<{ campaigns: SmsCampaign[] }> {
  return request<{ campaigns: SmsCampaign[] }>("/api/admin/sms/campaigns");
}

export async function adminFetchSmsBudget(): Promise<SmsBudgetPayload> {
  return request<SmsBudgetPayload>("/api/admin/settings/sms/budget");
}

export async function adminTopUpSmsBudget(
  body: SmsTopUpInput,
): Promise<SmsBudgetPayload> {
  return request<SmsBudgetPayload>("/api/admin/settings/sms/budget/top-up", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminFetchSmsMessages(params?: {
  status?: "PENDING" | "SENT" | "FAILED";
  from?: string;
  to?: string;
}): Promise<{ messages: SmsMessageLogEntry[] }> {
  const q = new URLSearchParams();
  if (params?.status) q.set("status", params.status);
  if (params?.from) q.set("from", params.from);
  if (params?.to) q.set("to", params.to);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return request<{ messages: SmsMessageLogEntry[] }>(`/api/admin/sms/messages${suffix}`);
}

export async function adminRetrySmsMessage(
  messageId: number,
): Promise<{
  sent: number;
  failed: number;
  recipients: number;
  costUgx: number;
  campaignId: number;
  message: SmsMessageLogEntry;
}> {
  return request(`/api/admin/sms/messages/${messageId}/retry`, { method: "POST" });
}

export async function adminFetchContactsBook(): Promise<AdminContactsPayload> {
  return request<AdminContactsPayload>("/api/admin/contacts-book");
}

export async function adminCreateBookContact(
  body: AdminCreateContactInput,
): Promise<AdminContact> {
  return request<AdminContact>("/api/admin/contacts-book", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminUpdateBookContact(
  id: number,
  body: AdminUpdateContactInput,
): Promise<AdminContact> {
  return request<AdminContact>(`/api/admin/contacts-book/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function adminDeleteBookContact(id: number): Promise<{ id: number }> {
  return request<{ id: number }>(`/api/admin/contacts-book/${id}`, {
    method: "DELETE",
  });
}

export async function adminDeleteBookContacts(ids: number[]): Promise<{ deleted: number }> {
  return request<{ deleted: number }>("/api/admin/contacts-book/delete", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

export async function adminBulkAddContacts(
  body: AdminBulkContactsInput,
): Promise<ContactImportResult> {
  return request<ContactImportResult>("/api/admin/contacts-book/bulk", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminImportContacts(
  body: AdminImportContactsInput,
): Promise<ContactImportResult> {
  return request<ContactImportResult>("/api/admin/contacts-book/import", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminSyncContacts(
  body: AdminSyncContactsInput,
): Promise<ContactSyncResult> {
  return request<ContactSyncResult>("/api/admin/contacts-book/sync", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminMergeContacts(
  body: AdminMergeContactsInput,
): Promise<ContactMergeResult> {
  return request<ContactMergeResult>("/api/admin/contacts-book/merge", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminFetchContactMerges(): Promise<AdminContactMerge[]> {
  return request<AdminContactMerge[]>("/api/admin/contacts-book/merges");
}

export async function adminUndoContactMerge(
  mergeId: number,
): Promise<{ survivorId: number; mergedId: number }> {
  return request<{ survivorId: number; mergedId: number }>(
    `/api/admin/contacts-book/merges/${mergeId}/undo`,
    { method: "POST" },
  );
}

export async function adminFetchContactDetail(id: number): Promise<AdminContactDetail> {
  return request<AdminContactDetail>(`/api/admin/contacts-book/${id}`);
}

export type MemberCheckInStatus = {
  event: {
    id: number;
    title: string | null;
    startsAt: string;
    endsAt: string;
    phase: "ongoing" | "upcoming" | "past";
  } | null;
  contact: {
    id: number | null;
    name: string;
    phone: string | null;
    email: string | null;
  };
  alreadyCheckedIn: boolean;
  attendeeId: number | null;
};

export async function fetchMemberCheckInStatus(): Promise<MemberCheckInStatus> {
  return request<MemberCheckInStatus>("/api/me/check-in/status");
}

export async function memberSelfCheckIn(): Promise<MemberCheckInResult> {
  return request<MemberCheckInResult>("/api/me/check-in", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function adminFetchYoutubeVideos(): Promise<{
  videos: YoutubeChannelVideo[];
  settings: YoutubeSettingsPublic;
}> {
  return request<{ videos: YoutubeChannelVideo[]; settings: YoutubeSettingsPublic }>(
    "/api/admin/youtube/videos",
  );
}

export async function adminSyncYoutubeChannel(): Promise<{
  synced: number;
  channelTitle: string;
  channelId: string;
  videos: YoutubeChannelVideo[];
  settings: YoutubeSettingsPublic;
}> {
  return request<{
    synced: number;
    channelTitle: string;
    channelId: string;
    videos: YoutubeChannelVideo[];
    settings: YoutubeSettingsPublic;
  }>("/api/admin/youtube/sync", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function adminUpdateYoutubeVideo(
  videoId: string,
  body: UpdateYoutubeVideoSiteMetaInput,
): Promise<YoutubeChannelVideo> {
  return request<YoutubeChannelVideo>(
    `/api/admin/youtube/videos/${encodeURIComponent(videoId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
}

export async function adminFetchUsers(): Promise<AdminUser[]> {
  return request<AdminUser[]>("/api/admin/users");
}

export async function adminCreateUser(body: AdminCreateUserInput): Promise<AdminUser> {
  return request<AdminUser>("/api/admin/users", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminUpdateUser(
  id: number,
  body: AdminUpdateUserInput,
): Promise<AdminUser> {
  return request<AdminUser>(`/api/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function adminResetUserPassword(
  id: number,
  body: AdminResetPasswordInput,
): Promise<{ id: number }> {
  return request<{ id: number }>(`/api/admin/users/${id}/reset-password`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminDeleteUser(id: number): Promise<{ id: number }> {
  return request<{ id: number }>(`/api/admin/users/${id}`, { method: "DELETE" });
}

export async function adminFetchEvents(): Promise<ScheduledEvent[]> {
  return request<ScheduledEvent[]>("/api/admin/events");
}

export async function adminFetchFeaturedEvent(): Promise<FeaturedEventInfo | null> {
  return request<FeaturedEventInfo | null>("/api/admin/events/featured");
}

export async function adminFetchEvent(id: number): Promise<ScheduledEvent> {
  return request<ScheduledEvent>(`/api/admin/events/${id}`);
}

export async function adminFetchEventHubSummary(id: number): Promise<AdminEventHubSummary> {
  return request<AdminEventHubSummary>(`/api/admin/events/${id}/summary`);
}

export async function adminCreateEvent(
  body: CreateScheduledEventInput,
): Promise<ScheduledEvent> {
  return request<ScheduledEvent>("/api/admin/events", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminUpdateEvent(
  id: number,
  patch: UpdateScheduledEventInput,
): Promise<ScheduledEvent> {
  return request<ScheduledEvent>(`/api/admin/events/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function adminDeleteEvent(id: number): Promise<void> {
  await request<unknown>(`/api/admin/events/${id}`, { method: "DELETE" });
}

/** Response shape from `POST /api/files/upload` (see server `fileToApi`). */
export type UploadedFileDto = {
  id: number;
  filename: string;
  originalName: string;
  size: number;
  mimetype: string;
  url: string;
  path: string;
  uploadsUrl: string;
  description: string;
  uploadedAt: string;
  formIdentifier: string | null;
  thumbnails: {
    small: string | null;
    medium: string | null;
    card: string | null;
  };
};

export type UploadFileOptions = {
  formIdentifier?: string;
  description?: string;
};

/** Multipart upload (session cookie). Sets public `uploadsUrl` for use in site content. */
export async function uploadFileWithAuth(
  file: File,
  opts?: UploadFileOptions,
): Promise<UploadedFileDto> {
  const fd = new FormData();
  fd.append("file", file);
  if (opts?.description) fd.append("description", opts.description);
  if (opts?.formIdentifier) fd.append("form_identifier", opts.formIdentifier);
  const res = await fetch("/api/files/upload", {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  let body: Envelope<UploadedFileDto>;
  try {
    body = (await res.json()) as Envelope<UploadedFileDto>;
  } catch {
    throw new ApiError(`Bad response (${res.status})`, res.status);
  }
  if (!body.ok) {
    throw new ApiError(
      body.error || `Upload failed (${res.status})`,
      res.status,
      body.details,
    );
  }
  return body.data;
}

/** List uploaded files (auth required). Newest first. */
export async function fetchUploadedFiles(): Promise<UploadedFileDto[]> {
  return request<UploadedFileDto[]>("/api/files/");
}

// --- videos (public) ---

export async function fetchVideos(opts?: {
  category?: string;
}): Promise<SiteVideoPublic[]> {
  const q = opts?.category?.trim()
    ? `?category=${encodeURIComponent(opts.category.trim())}`
    : "";
  const data = await request<{ videos: SiteVideoPublic[] }>(`/api/videos${q}`);
  return data.videos;
}

export async function fetchVideo(videoId: string): Promise<SiteVideoPublic> {
  return request<SiteVideoPublic>(`/api/videos/${encodeURIComponent(videoId)}`);
}

export async function fetchVideoComments(videoId: string): Promise<LiveComment[]> {
  return request<LiveComment[]>(
    `/api/videos/${encodeURIComponent(videoId)}/comments`,
  );
}

export async function postVideoComment(
  videoId: string,
  body: LiveCommentInput,
): Promise<LiveComment> {
  return request<LiveComment>(
    `/api/videos/${encodeURIComponent(videoId)}/comments`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

// --- gallery (public) ---

export async function fetchGalleryAlbums(): Promise<GalleryAlbumSummary[]> {
  return request<GalleryAlbumSummary[]>("/api/gallery/albums");
}

export async function fetchGalleryAlbum(slug: string): Promise<GalleryAlbumDetail> {
  return request<GalleryAlbumDetail>(`/api/gallery/albums/${encodeURIComponent(slug)}`);
}

/** Admin-only preview of an album by slug (works when the album is not published yet). */
export async function adminFetchGalleryAlbumBySlug(slug: string): Promise<GalleryAlbumDetail> {
  return request<GalleryAlbumDetail>(
    `/api/admin/gallery/albums/by-slug/${encodeURIComponent(slug)}`,
  );
}

export async function fetchPhotoComments(photoId: number): Promise<PhotoComment[]> {
  return request<PhotoComment[]>(`/api/gallery/photos/${photoId}/comments`);
}

export async function postPhotoComment(
  photoId: number,
  body: PhotoCommentInput,
): Promise<PhotoComment> {
  return request<PhotoComment>(`/api/gallery/photos/${photoId}/comments`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function deletePhotoComment(photoId: number, commentId: number): Promise<void> {
  await request<unknown>(`/api/gallery/photos/${photoId}/comments/${commentId}`, {
    method: "DELETE",
  });
}

// --- gallery (admin) ---

export async function adminFetchGalleryAlbums(): Promise<AdminGalleryAlbum[]> {
  return request<AdminGalleryAlbum[]>("/api/admin/gallery/albums");
}

export async function adminFetchGalleryPhotos(albumId: number): Promise<AdminGalleryPhoto[]> {
  return request<AdminGalleryPhoto[]>(`/api/admin/gallery/albums/${albumId}/photos`);
}

export async function adminCreateGalleryAlbum(
  body: CreateGalleryAlbumInput,
): Promise<AdminGalleryAlbum> {
  return request<AdminGalleryAlbum>("/api/admin/gallery/albums", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminUpdateGalleryAlbum(
  id: number,
  body: UpdateGalleryAlbumInput,
): Promise<AdminGalleryAlbum> {
  return request<AdminGalleryAlbum>(`/api/admin/gallery/albums/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function adminDeleteGalleryAlbum(id: number): Promise<void> {
  await request<unknown>(`/api/admin/gallery/albums/${id}`, { method: "DELETE" });
}

/** Fetch watermarked album photos as a zip blob (cover photo first). */
export async function fetchGalleryAlbumZipBlob(
  albumId: number,
): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`/api/admin/gallery/albums/${albumId}/download-zip`, {
    credentials: "include",
  });
  if (!res.ok) {
    let msg = `Download failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) msg = body.error;
    } catch {
      /* response may not be JSON */
    }
    throw new ApiError(msg, res.status);
  }

  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? `album-${albumId}-watermarked.zip`;
  return { blob, filename };
}

export function triggerBrowserFileDownload(url: string, filename: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
}

/** Download all watermarked master JPEGs in the album as a zip (cover photo first). */
export async function adminDownloadGalleryAlbumZip(albumId: number): Promise<void> {
  const { blob, filename } = await fetchGalleryAlbumZipBlob(albumId);
  const url = URL.createObjectURL(blob);
  triggerBrowserFileDownload(url, filename);
  URL.revokeObjectURL(url);
}

export async function adminAddGalleryPhoto(
  albumId: number,
  body: CreateGalleryPhotoInput,
): Promise<AdminGalleryPhoto> {
  return request<AdminGalleryPhoto>(`/api/admin/gallery/albums/${albumId}/photos`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Watermark, generate derivatives, and add photos to an album (multipart). */
export async function adminUploadGalleryPhotos(
  albumId: number,
  files: File[],
): Promise<AdminGalleryPhoto[]> {
  const fd = new FormData();
  for (const file of files) {
    fd.append("files", file);
  }
  const res = await fetch(`/api/admin/gallery/albums/${albumId}/photos/upload`, {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  let body: Envelope<AdminGalleryPhoto[]>;
  try {
    body = (await res.json()) as Envelope<AdminGalleryPhoto[]>;
  } catch {
    throw new ApiError(`Bad response (${res.status})`, res.status);
  }
  if (!body.ok) {
    throw new ApiError(
      body.error || `Upload failed (${res.status})`,
      res.status,
      body.details,
    );
  }
  return body.data;
}

/** Extract images from a zip, watermark, and add to an album (max 500 images per zip). */
export async function adminUploadGalleryZip(
  albumId: number,
  file: File,
): Promise<{ photos: AdminGalleryPhoto[]; extracted: number; skipped: number }> {
  const fd = new FormData();
  fd.append("zip", file);
  const res = await fetch(`/api/admin/gallery/albums/${albumId}/photos/upload-zip`, {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  let body: Envelope<AdminGalleryPhoto[]> & {
    meta?: { extracted: number; skipped: number };
  };
  try {
    body = (await res.json()) as typeof body;
  } catch {
    throw new ApiError(`Bad response (${res.status})`, res.status);
  }
  if (!body.ok) {
    throw new ApiError(
      body.error || `Zip upload failed (${res.status})`,
      res.status,
      body.details,
    );
  }
  return {
    photos: body.data,
    extracted: body.meta?.extracted ?? body.data.length,
    skipped: body.meta?.skipped ?? 0,
  };
}

export async function adminUpdateGalleryPhoto(
  id: number,
  body: UpdateGalleryPhotoInput,
): Promise<AdminGalleryPhoto> {
  return request<AdminGalleryPhoto>(`/api/admin/gallery/photos/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function adminDeleteGalleryPhoto(id: number): Promise<void> {
  await request<unknown>(`/api/admin/gallery/photos/${id}`, { method: "DELETE" });
}

export async function adminReorderGalleryPhotos(
  albumId: number,
  photoIds: number[],
): Promise<AdminGalleryPhoto[]> {
  return request<AdminGalleryPhoto[]>(`/api/admin/gallery/albums/${albumId}/reorder`, {
    method: "POST",
    body: JSON.stringify({ photoIds }),
  });
}

export async function adminFetchPhotoComments(
  status?: PhotoCommentStatus | "ALL",
): Promise<AdminPhotoComment[]> {
  const q = status && status !== "ALL" ? `?status=${status}` : "";
  return request<AdminPhotoComment[]>(`/api/admin/gallery/comments${q}`);
}

export async function adminModeratePhotoComment(
  id: number,
  status: "PUBLISHED" | "REJECTED",
): Promise<AdminPhotoComment> {
  return request<AdminPhotoComment>(`/api/admin/gallery/comments/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function adminDeletePhotoComment(id: number): Promise<void> {
  await request<unknown>(`/api/admin/gallery/comments/${id}`, { method: "DELETE" });
}

// --- event attendees / check-in ---

export async function adminFetchAttendeeSummary(): Promise<EventAttendeeSummary[]> {
  return request<EventAttendeeSummary[]>("/api/admin/attendees/summary");
}

export async function adminFetchEventAttendees(
  eventId: number,
  q?: string,
): Promise<EventAttendee[]> {
  const query = q?.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
  return request<EventAttendee[]>(`/api/admin/attendees/events/${eventId}${query}`);
}

export async function adminCreateEventAttendee(
  eventId: number,
  body: CreateEventAttendeeInput,
): Promise<EventAttendee> {
  return request<EventAttendee>(`/api/admin/attendees/events/${eventId}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminBulkCreateEventAttendees(
  eventId: number,
  body: BulkCreateEventAttendeesInput,
): Promise<EventAttendee[]> {
  return request<EventAttendee[]>(`/api/admin/attendees/events/${eventId}/bulk`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminUpdateEventAttendee(
  id: number,
  body: UpdateEventAttendeeInput,
): Promise<EventAttendee> {
  return request<EventAttendee>(`/api/admin/attendees/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function adminCheckInAttendee(id: number): Promise<EventAttendee> {
  return request<EventAttendee>(`/api/admin/attendees/${id}/check-in`, {
    method: "POST",
  });
}

export async function adminCheckOutAttendee(id: number): Promise<EventAttendee> {
  return request<EventAttendee>(`/api/admin/attendees/${id}/check-out`, {
    method: "POST",
  });
}

export async function adminDeleteEventAttendee(id: number): Promise<void> {
  await request<unknown>(`/api/admin/attendees/${id}`, { method: "DELETE" });
}
